/**
 * @author      Shivani Mehra
 * @date        July, 2025
 * @copyright   www.ibirdsservices.com
 */

import React, { useEffect, useState } from "react";
import { Col, Form, Modal, Row } from "react-bootstrap";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import { toast } from "react-toastify"; //npm i react-toastify --force
import "react-toastify/dist/ReactToastify.css";
import TemplatePreview from "../whatsapp_template/TemplatePreview";
import jwt_decode from "jwt-decode";

// import moment from 'moment-timezone';

const TemplateModal = ({
  show,
  onHide,
  contactData,
  refreshData,
  filterData,
  selectedWhatsAppSetting,
}) => {
  const [templateRates, setTemplateRates] = useState([]);
  const [userInfo, setUserInfo] = useState(
    jwt_decode(sessionStorage.getItem("token")),
  );
  const [selectedCategory, setSelectedCategory] = useState("");
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [allTemplateData, setAllTemplateData] = useState([]);
  const [selectedTemplateName, setSelectedTemplateName] = useState();
  const [isSpinner, setIsSpinner] = useState(false);
  // const categories = ["AUTHENTICATION", "UTILITY", "MARKETING"];
  const categories = [
    "ALL",
    "UTILITY",
    "MARKETING",
    "IMAGE",
    "VIDEO",
    "DOCUMENT",
    "CAROUSEL",
    "NO_ATTACHMENT",
  ];

  const [parameters, setParameters] = useState({});
  const [buttonVariableValues, setButtonVariableValues] = useState({});

  useEffect(() => {
    // console.log("userInfo",userInfo)
    fetchtemplateRates();
    fetchAllTemplate(selectedWhatsAppSetting);
  }, [selectedWhatsAppSetting]);

  const fetchtemplateRates = async () => {
    try {
      const templateRates = await WhatsAppAPI.templateRates();
      if (templateRates.success) {
        setTemplateRates(templateRates.records);
      } else {
        setTemplateRates([]);
      }
    } catch (error) {
      toast.error("Error fetching data.");
    }
  };
  const handleCategoryChange = (e) => {
    const category = e.target.value;
    setSelectedCategory(category);
    setSelectedTemplateName(null);

    let filtered = allTemplateData;

    if (category && category !== "ALL") {
      filtered = allTemplateData.filter((template) => {
        // Check by business category (MARKETING / UTILITY / AUTHENTICATION)
        if (["AUTHENTICATION", "UTILITY", "MARKETING"].includes(category)) {
          return template.category === category;
        }

        // Check by type of attachment
        if (category === "IMAGE") {
          return (
            template.header === "IMAGE" ||
            template.cards?.some((c) => c.header_type === "IMAGE")
          );
        }
        if (category === "VIDEO") {
          return (
            template.header === "VIDEO" ||
            template.cards?.some((c) => c.header_type === "VIDEO")
          );
        }
        if (category === "DOCUMENT") {
          return (
            template.header === "DOCUMENT" ||
            template.cards?.some((c) => c.header_type === "DOCUMENT")
          );
        }
        if (category === "CAROUSEL") {
          return template.template_type === "carousel";
        }

        // Check if parameterized (has {{}})
        if (category === "PARAMETERIZED") {
          return (
            template.message_body?.includes("{{") ||
            template.cards?.some((c) => c.body_text?.includes("{{"))
          );
        }

        // Templates without attachments
        if (category === "NO_ATTACHMENT") {
          return (
            (!template.header || template.header === "TEXT") &&
            !template.template_type &&
            !template.header_image_url &&
            !template.header_document_url &&
            !template.header_video_url
          );
        }

        return false;
      });
    }

    setFilteredTemplates(filtered);
  };

  const fetchAllTemplate = async (selectedWhatsAppSetting) => {
    const result = await WhatsAppAPI.getApprovedTemplates(
      selectedWhatsAppSetting,
    );

    if (result.error) {
      setAllTemplateData([]);
      setIsSpinner(true);
    } else {
      if (result?.data && result) {
        const transformedData = result?.data.map((row) => {
          // Check for CAROUSEL type
          const carouselComp = row.components.find(
            (component) =>
              component.type === "CAROUSEL" || component.type === "carousel",
          );
          const mainBody = row.components.find(
            (component) => component.type === "BODY",
          );

          if (carouselComp && Array.isArray(carouselComp.cards)) {
            // Map each card to a normalized structure
            const cards = carouselComp.cards.map((card) => {
              const header =
                card.components.find((c) => c.type === "HEADER") || {};
              const body = card.components.find((c) => c.type === "BODY") || {};
              const buttonsComp = card.components.find(
                (c) => c.type === "BUTTONS",
              );
              const buttons = buttonsComp?.buttons || [];

              const cardObj = {
                header_type: header.format || "",
                header_text: header.format === "TEXT" ? header.text : "",
                header_image_url:
                  header.format === "IMAGE"
                    ? header.example?.header_handle?.[0] || header.handle || ""
                    : "",
                header_document_url:
                  header.format === "DOCUMENT"
                    ? header.example?.header_handle?.[0] || ""
                    : "",
                header_video_url:
                  header.format === "VIDEO"
                    ? header.example?.header_handle?.[0] || ""
                    : "",
                body_text: body.text || "",
                body_text_example: body.example?.body_text?.[0] || [],
                buttons: buttons
                  .map((element) => ({
                    type: element.type,
                    text: element.text,
                    ...(element.type === "PHONE_NUMBER" && {
                      phone_number: element.phone_number,
                    }),
                    ...(element.type === "URL" && { url: element.url }),
                    example: element.example?.[0] || "",
                  }))
                  .filter((item) => item.text && item.type),
              };
              return cardObj;
            });

            const transformed = {
              id: row.id,
              name: row.name,
              templatename: row.name.replace(/_/g, " "),
              language: row.language,
              status: row.status,
              category: row.category,
              template_type: "carousel",
              message_body: mainBody?.text || "",
              example_body_text: mainBody?.example?.body_text || [],
              cards, // <-- This is the important part!
            };

            return transformed;
          }

          const header =
            row.components.find((component) => component.type === "HEADER") ||
            {};
          const body =
            row.components.find((component) => component.type === "BODY") || {};
          const footer =
            row.components.find((component) => component.type === "FOOTER") ||
            {};
          const buttons =
            row.components.find((component) => component.type === "BUTTONS")
              ?.buttons || [];

          return {
            id: row.id,
            name: row.name,
            templatename: row.name.replace(/_/g, " "),
            language: row.language,
            status: row.status,
            category: row.category,
            header: header.format || "",

            header_text: header.format === "TEXT" ? header.text : "",
            header_image_url:
              header.format === "IMAGE"
                ? header.example?.header_handle?.[0] || ""
                : "",
            header_document_url:
              header.format === "DOCUMENT"
                ? header.example?.header_handle?.[0] || ""
                : "",
            header_video_url:
              header.format === "VIDEO"
                ? header.example?.header_handle?.[0] || ""
                : "",

            message_body: body.text || "",
            example_body_text: body.example?.body_text || [],
            footer: footer.text || "",

            add_security_recommendation:
              body.add_security_recommendation || false,
            code_expiration_minutes: footer.code_expiration_minutes || null,
            buttons: buttons
              .map((element) => ({
                type: element.type,
                text: element.text,
                ...(element.type === "PHONE_NUMBER" && {
                  phone_number: element.phone_number,
                }),
                ...(element.type === "URL" && { url: element.url }),
              }))
              .filter((item) => item.text && item.type),
          };
        });

        setAllTemplateData(transformedData);
        setFilteredTemplates(transformedData);
        setIsSpinner(true);
      }
      setIsSpinner(true);
    }
  };
  function generateOTP() {
    return Math.floor(100000 + Math.random() * 900000).toString();
  }

  const handleChange = async (event) => {
    const selectedName = event.target.value;

    const template = allTemplateData.find((t) => t.id === selectedName);

    if (template) {
      if (template.template_type === "carousel") {
        // Pass the whole object as-is for carousel
        setSelectedTemplateName(template);
        const buttonVars = getButtonVariables(template);
        setButtonVariableValues(
          buttonVars.reduce(
            (acc, v) => ({ ...acc, [v.variableIndex]: "" }),
            {},
          ),
        );
      } else {
        const {
          id,
          name,
          language,
          category,
          header,
          header_text,
          header_image_url,
          header_document_url,
          header_video_url,
          message_body,
          example_body_text,
          footer,
          buttons,
        } = template;
        // const exampleValue = example_body_text.length > 0 && example_body_text[0].length > 0 ? generateOTP() : '';
        const examplebodytext =
          category === "AUTHENTICATION" ? generateOTP() : "";

        const header_body =
          header_text ||
          header_image_url ||
          header_document_url ||
          header_video_url;
        const formattedTemplate = {
          id,
          name,
          language,
          category,
          header,
          header_body,
          message_body,
          example_body_text: examplebodytext,
          footer,
          buttons,
          business_number: selectedWhatsAppSetting,
        };
        setSelectedTemplateName(formattedTemplate);
        const buttonVars = getButtonVariables(formattedTemplate);
        setButtonVariableValues(
          buttonVars.reduce(
            (acc, v) => ({ ...acc, [v.variableIndex]: "" }),
            {},
          ),
        );
      }
    } else {
      setSelectedTemplateName(null);
      setButtonVariableValues({});
    }
  };

  // Function to fetch the file and convert it to a Blob
  async function fetchFile(url) {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Network response was not ok");
    return await response.blob(); // Return the Blob
  }

  const handleParametersChange = (newParams) => {
    setParameters(newParams);
    // console.log("newParams",newParams);
  };

  /** Returns list of button dynamic variables (e.g. {{1}}) for the given template */
  function getButtonVariables(template) {
    if (!template) return [];
    const seen = new Set();
    const result = [];

    const collectFromButtons = (buttons, prefix = "") => {
      if (!Array.isArray(buttons)) return;
      buttons.forEach((btn, btnIdx) => {
        const text = btn.text || "";
        const url = (btn.url || "").toString();
        const combined = `${text} ${url}`;
        const matches = combined.match(/\{\{(\d+)\}\}/g);
        if (matches) {
          matches.forEach((m) => {
            const variableIndex = m.replace(/\{\{|\}\}/g, "");
            if (!seen.has(variableIndex)) {
              seen.add(variableIndex);
              result.push({
                variableIndex,
                label: `Button value for {{${variableIndex}}}`,
              });
            }
          });
        }
      });
    };

    if (
      template.template_type === "carousel" &&
      Array.isArray(template.cards)
    ) {
      template.cards.forEach((card, cardIdx) => {
        collectFromButtons(card.buttons, `Card ${cardIdx + 1} - `);
      });
    } else if (Array.isArray(template.buttons)) {
      collectFromButtons(template.buttons);
    }

    return result.sort(
      (a, b) => Number(a.variableIndex) - Number(b.variableIndex),
    );
  }

  function calculateMessageCost(countryCode, category, templateRates) {
    // Ensure the countryCode is a clean string (e.g., remove '+' if present)
    const cleanedCountryCode = countryCode.replace(/^\+/, "");

    // Match rate from the templateRates list
    const matchedRate = templateRates.find(
      (rate) => rate.country_code === cleanedCountryCode,
    );

    if (!matchedRate) {
      console.warn("No matching country code found. Defaulting to 0.");
      return 0;
    }

    const lowerCategory = category?.toLowerCase();
    let price = 0;

    if (lowerCategory === "marketing") {
      price = parseFloat(matchedRate.marketing || 0);
    } else if (
      lowerCategory === "utility" ||
      lowerCategory === "authentication"
    ) {
      price = parseFloat(matchedRate.utility || 0);
    }

    return price;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    // console.log("parameters", parameters);
    // console.log("selected", selectedTemplateName);
    const cardDocIds = [];
    const cardFileResults = [];
    setIsSpinner(false);
    // ✅ Handle carousel card files if cards exist
    if (
      Array.isArray(selectedTemplateName.cards) &&
      selectedTemplateName.cards.length > 0
    ) {
      for (let idx = 0; idx < selectedTemplateName.cards.length; idx++) {
        const cardParams = parameters[idx] || {};
        let docId = null;
        let fileResult = null;

        if (cardParams.file && cardParams.file instanceof File) {
          const formData = new FormData();
          formData.append("messaging_product", "whatsapp");
          formData.append("file", cardParams.file);
          formData.append("description", `Carousel Card ${idx + 1} Attachment`);

          fileResult = await WhatsAppAPI.createFile(contactData.id, formData);

          if (fileResult?.records?.[0]?.id) {
            parameters[idx].file_id = fileResult.records[0].id;
            parameters[idx].file_title = fileResult.records[0].title;
          }
        } else if (cardParams.fileURL && cardParams.fileType) {
          try {
            const response = await fetch(cardParams.fileURL);
            const blob = await response.blob();
            const fileName = `carousel_card_${idx + 1}.${cardParams.fileType}`;
            const file = new File([blob], fileName, { type: blob.type });

            const formData = new FormData();
            formData.append("messaging_product", "whatsapp");
            formData.append("file", file);
            formData.append(
              "description",
              `Carousel Card ${idx + 1} Attachment`,
            );

            fileResult = await WhatsAppAPI.createFile(contactData.id, formData);

            if (fileResult?.records?.[0]?.id) {
              parameters[idx].file_id = fileResult.records[0].id;
              parameters[idx].file_title = fileResult.records[0].title;
            }
          } catch (error) {
            console.error(`Error processing file for card ${idx + 1}:`, error);
            toast.error(`Error processing file for card ${idx + 1}`);
          }
        }

        cardDocIds.push(docId);
        cardFileResults.push(fileResult);
      }
    }

    // ✅ If it's not a carousel, but a single media file
    if (parameters.file && parameters.file instanceof File) {
      try {
        const formData = new FormData();
        formData.append("messaging_product", "whatsapp");
        formData.append("file", parameters.file);
        formData.append("description", "Single Attachment");

        const fileResult = await WhatsAppAPI.createFile(
          contactData.id,
          formData,
        );

        if (fileResult?.records?.[0]?.id) {
          parameters.file_id = fileResult.records[0].id;
          parameters.file_title = fileResult.records[0].title;
        }
      } catch (error) {
        console.error("Error uploading single media file:", error);
        toast.error("Error uploading media");
      }
    }

    const countryCode =
      contactData.countrycode || contactData.country_code || "91";
    const rawNumber = contactData.whatsapp_number.replace(/^[+]/, "");
    const fullNumber = contactData.whatsapp_number.startsWith("+")
      ? contactData.whatsapp_number
      : `${
          countryCode.startsWith("+") ? countryCode : "+" + countryCode
        }${rawNumber}`;

    const parametersWithButtons = {
      ...parameters,
      ...(Object.keys(buttonVariableValues).length > 0 && {
        button_variables: buttonVariableValues,
      }),
    };

    const requestBody = {
      id: selectedTemplateName.id,
      name: selectedTemplateName.name,
      contact_name: contactData.contactname,
      whatsapp_number: fullNumber,
      amount: calculateMessageCost(
        countryCode,
        selectedTemplateName.category,
        templateRates,
      ),
      parameters: parametersWithButtons,
    };

    const result = await WhatsAppAPI.sendWhatsAppTemplate(
      requestBody,
      selectedWhatsAppSetting,
    );

    if (result?.error) {
      toast.error(result?.error);
    } else {
      toast.success("Template sent successfully!");
      setIsSpinner(true);
      refreshData();
    }
  };

  // const handleSubmit = async (event) => {
  //         //console.log("selectedTemplateName",selectedTemplateName)
  //          //console.log("parameters",parameters)

  //   event.preventDefault();
  //   let fullNumber;
  //   if (filterData?.recordType === "recentlyMessage") {
  //     fullNumber = contactData.whatsapp_number?.startsWith("+")
  //       ? contactData.whatsapp_number
  //       : `+${contactData.whatsapp_number}`;
  //   } else {
  //     const rawNumber = contactData.whatsapp_number
  //       ?.toString()
  //       .replace(/^[+]/, "");
  //     const countryCode = (contactData.country_code || "91").replace(
  //       /^[+]?/,
  //       "+"
  //     );
  //     fullNumber = `${countryCode}${rawNumber}`;
  //   }
  //   let documentId = null;
  //   let fileResult = null;
  //   setIsSpinner(false);

  //   // --- CAROUSEL SUPPORT ---
  //   if (
  //     selectedTemplateName.template_type === "carousel" &&
  //     Array.isArray(selectedTemplateName.cards)
  //   ) {
  //     if (!isCarouselFormValid(selectedTemplateName, parameters)) {
  //       toast.error(
  //         "Please fill all required fields for main body and all cards before sending."
  //       );
  //       return;
  //     }

  //     try {
  //       // Step 1: Create Message Template Data
  //       const price = calculateMessageCost(
  //         fullNumber,
  //         selectedTemplateName.category,
  //         templateRates
  //       );

  //       if (userInfo?.has_wallet) {
  //         const tenantcode = userInfo.tenantcode;
  //         if (!tenantcode) {
  //           toast.error("Tenant code not found.");
  //           return;
  //         }
  //         const balanceCheckPayload = { amount: price, tenantcode };
  //         const balanceRes = await WhatsAppAPI.checkBalance(balanceCheckPayload);
  //         if (!balanceRes.success || !balanceRes.sufficient) {
  //           setIsSpinner(true);
  //           toast.error(balanceRes.message || "Insufficient balance.");
  //           return;
  //         }
  //       }

  //       // Prepare carousel template data for createMessageTemplateData
  //       const carouselTemplateData = {
  //         template_id: selectedTemplateName.id,
  //         template_name: selectedTemplateName.name,
  //         language: selectedTemplateName.language,
  //         category: selectedTemplateName.category,
  //         header: null, // Carousel doesn't have a main header
  //         header_body: null,
  //         message_body: selectedTemplateName.message_body || "",
  //         example_body_text: parameters, // Store all parameters as JSON
  //         footer: null,
  //         buttons: null,
  //         business_number: selectedWhatsAppSetting,
  //       cards: selectedTemplateName.cards,
  //         template_type: "carousel"
  //       };
  //       const msgResult = await WhatsAppAPI.createMessageTemplateData(
  //         carouselTemplateData
  //       );
  //       if (msgResult?.errors) {
  //         toast.error(msgResult?.errors);
  //         setIsSpinner(true);
  //         return;
  //       }

  //       // Step 2: Upload files for each card if present, collect doc IDs and file results
  //       const cardDocIds = [];
  //       const cardFileResults = [];

  //       for (let idx = 0; idx < selectedTemplateName.cards.length; idx++) {
  //         const cardParams = parameters[idx] || {};
  //         let docId = null;
  //         let fileResult = null;
  //     //console.log("cardParams",cardParams)
  //         if (cardParams.file && cardParams.file instanceof File) {
  //           // Create file record first
  //           const formData = new FormData();
  //           formData.append("messaging_product", "whatsapp");
  //           formData.append("file", cardParams.file);
  //           formData.append("description", `Carousel Card ${idx + 1} Attachment`);

  //           fileResult = await WhatsAppAPI.createFile(contactData.id, formData);

  //           // Then upload to WhatsApp API
  //           const uploadResponse = await WhatsAppAPI.uploadDocumentWithApi(
  //             formData,
  //             selectedWhatsAppSetting
  //           );
  //           if (uploadResponse?.id) {
  //             docId = uploadResponse.id;
  //           }
  //         } else if (cardParams.fileURL && cardParams.fileType) {
  //       //console.log("cardParams.fileURL && cardParams.fileType",cardParams.fileURL && cardParams.fileType)
  //           // Handle fileURL case - convert blob URL to File object
  //           try {
  //             const response = await fetch(cardParams.fileURL);
  //             const blob = await response.blob();
  //             const fileName = `carousel_card_${idx + 1}.${cardParams.fileType}`;
  //             const file = new File([blob], fileName, { type: blob.type });

  //             // Create file record first
  //             const formData = new FormData();
  //             formData.append("messaging_product", "whatsapp");
  //             formData.append("file", file);
  //             formData.append("description", `Carousel Card ${idx + 1} Attachment`);

  //             fileResult = await WhatsAppAPI.createFile(contactData.id, formData);

  //             // Then upload to WhatsApp API
  //             const uploadResponse = await WhatsAppAPI.uploadDocumentWithApi(
  //               formData,
  //               selectedWhatsAppSetting
  //             );
  //             if (uploadResponse?.id) {
  //               docId = uploadResponse.id;
  //             }
  //           } catch (error) {
  //             console.error(`Error processing file for card ${idx + 1}:`, error);
  //             toast.error(`Error processing file for card ${idx + 1}`);
  //           }
  //         }
  //         cardDocIds.push(docId);
  //         cardFileResults.push(fileResult);
  //       }

  //       // Step 3: Build main body parameters (if any)
  //       let mainBodyParams = [];
  //       if (selectedTemplateName.message_body) {
  //         const mainMatches =
  //           selectedTemplateName.message_body.match(/\{\{([^}]+)\}\}/g) || [];
  //         const mainUniqueIndices = [
  //           ...new Set(mainMatches.map((m) => m.replace(/\{\{|\}\}/g, ""))),
  //         ];
  //         mainBodyParams = mainUniqueIndices.map((paramIdx) => ({
  //           type: "text",
  //           text: (parameters.main && parameters.main[paramIdx]) || "",
  //         }));
  //       }

  //       // Step 4: Build carousel cards payload
  //       const cardsPayload = selectedTemplateName.cards.map((card, idx) => {
  //         const cardParams = parameters[idx] || {};
  //         // Find curly-brace params in card body
  //         const matches =
  //           (typeof card.body_text === "string" ? card.body_text : "").match(
  //             /\{\{([^}]+)\}\}/g
  //           ) || [];
  //         const uniqueIndices = [
  //           ...new Set(matches.map((m) => m.replace(/\{\{|\}\}/g, ""))),
  //         ];
  //         // Build body parameters
  //         const bodyParams = uniqueIndices.map((paramIdx) => ({
  //           type: "text",
  //           text: cardParams[paramIdx] || "",
  //         }));
  //         // Build header parameter if file uploaded
  //         let headerParams = [];
  //         if (card.header_type !== "TEXT" && cardDocIds[idx]) {
  //           headerParams = [
  //             {
  //               type: card.header_type.toLowerCase(),
  //               [card.header_type.toLowerCase()]: {
  //                 id: cardDocIds[idx],
  //                 ...(card.header_type === "DOCUMENT" &&
  //                 ((cardParams.file && cardParams.file.name) ||
  //                  (cardParams.fileURL && cardParams.fileType))
  //                   ? {
  //                       filename: cardParams.file?.name ||
  //                               `carousel_card_${idx + 1}.${cardParams.fileType}`
  //                     }
  //                   : {}),
  //               },
  //             },
  //           ];
  //         }
  //         // Build card components array
  //         const cardComponents = [];
  //         if (headerParams.length > 0) {
  //           cardComponents.push({ type: "header", parameters: headerParams });
  //         }
  //         if (bodyParams.length > 0) {
  //           cardComponents.push({ type: "body", parameters: bodyParams });
  //         }
  //         return {
  //           card_index: idx,
  //           components: cardComponents,
  //         };
  //       });

  //       // Step 5: Build API payload for carousel
  //       const componentsArr = [];
  //       if (mainBodyParams.length > 0) {
  //         componentsArr.push({ type: "body", parameters: mainBodyParams });
  //       }
  //       componentsArr.push({ type: "carousel", cards: cardsPayload });

  //       if (hasEmptyParameterDeep(componentsArr)) {
  //         toast.error(
  //           "Please fill all required fields for main body and all cards. No empty values allowed."
  //         );
  //         return;
  //       }

  //       const reqBody = {
  //         messaging_product: "whatsapp",
  //         recipient_type: "individual",
  //         to: fullNumber,
  //         type: "template",
  //         template: {
  //           name: selectedTemplateName.name,
  //           language: { code: selectedTemplateName.language },
  //           components: componentsArr,
  //         },
  //       };

  //       const result = await WhatsAppAPI.sendWhatsAppTemplateMessage(
  //         reqBody,
  //         selectedWhatsAppSetting,
  //         selectedTemplateName.category
  //       );

  //       if (result.error) {
  //         const details =
  //           result.error?.error_data?.details ||
  //           result.error?.message ||
  //           result.error?.body ||
  //           JSON.stringify(result.error);
  //         toast.error(`Error: ${details}`);
  //       } else {
  //         const messageId = result.messages[0].id;

  //         // Step 6: Insert message history record
  //         const msgHistoryResult = await WhatsAppAPI.insertMsgHistoryRecords({
  //           parent_id: contactData.id || null,
  //           name: contactData?.contactname || "",
  //           message_template_id: msgResult.id || null,
  //           whatsapp_number: fullNumber,
  //           message: "",
  //           status: "Outgoing",
  //           recordtypename: filterData?.recordType || "",
  //           file_id: cardFileResults[0]?.records[0]?.id || null, // Use first file if available
  //           is_read: true,
  //           business_number: selectedWhatsAppSetting,
  //           message_id: messageId,
  //           interactive_id: null,
  //         });

  //         // Step 7: Insert campaign parameters
  //         const { main, ...restParameters } = parameters || {};
  //         if (Object.keys(restParameters).length > 0 || (main && Object.keys(main).length > 0)) {
  //           // Create array of file IDs from createFile API
  //           const fileIds = cardFileResults
  //             .filter(fileResult => fileResult?.records?.[0]?.id)
  //             .map(fileResult => fileResult.records[0].id);

  //           // Combine main and rest parameters for body_text_params
  //           const bodyTextParams = {
  //             ...(main && Object.keys(main).length > 0 ? { main } : {}),
  //             ...restParameters
  //           };

  //           const campaign_template_params = {
  //             campaign_id: null,
  //             body_text_params: bodyTextParams,
  //             msg_history_id: msgHistoryResult?.id || null,
  //             file_ids: JSON.stringify(fileIds), // Convert array to JSON string
  //             whatsapp_number_admin: userInfo?.whatsapp_number || null,
  //           };

  //           const campaignparamsResult =
  //             await WhatsAppAPI.insertCampaignParamsRecords(
  //               campaign_template_params
  //             );
  //         }

  //         // Step 8: Handle wallet transaction
  //         if (userInfo?.has_wallet) {
  //           const balanceRes = await WhatsAppAPI.createWalletTransaction({
  //             amount: price,
  //             reason: `debited for sending carousel template to ${contactData?.contactname}`,
  //             type: "debit",
  //             status: "paid",
  //             tenantcode: userInfo.tenantcode,
  //           });
  //         }

  //         toast.success("Carousel template sent successfully!");
  //       }
  //     } catch (error) {
  //       console.error("Carousel template error:", error);
  //       toast.error("Error sending carousel template");
  //     } finally {
  //       setIsSpinner(true);
  //       refreshData();
  //     }
  //     return;
  //   }
  //   // --- END CAROUSEL SUPPORT ---

  //   // --- EXISTING LOGIC FOR NON-CAROUSEL ---
  //   if (selectedTemplateName.category === "AUTHENTICATION") {
  //     selectedTemplateName.example_body_text = parameters["1"];
  //   } else {
  //     const { file, ...filteredParams } = parameters;
  //     selectedTemplateName.example_body_text = filteredParams;
  //   }
  //   try {
  //     // Step 1: Create Message Template Data

  //     const price = calculateMessageCost(
  //       fullNumber,
  //       selectedTemplateName.category,
  //       templateRates
  //     );

  //     if (userInfo?.has_wallet) {
  //       const tenantcode = userInfo.tenantcode;
  //       if (!tenantcode) {
  //         toast.error("Tenant code not found.");
  //         return;
  //       }
  //       const balanceCheckPayload = { amount: price, tenantcode };
  //       const balanceRes = await WhatsAppAPI.checkBalance(balanceCheckPayload);
  //       if (!balanceRes.success || !balanceRes.sufficient) {
  //         setIsSpinner(true);

  //         toast.error(balanceRes.message || "Insufficient balance.");
  //         return;
  //       }
  //     }
  //     const msgResult = await WhatsAppAPI.createMessageTemplateData(
  //       selectedTemplateName
  //     );
  //     if (msgResult?.errors) {
  //       toast.error(msgResult?.errors);
  //       setIsSpinner(true);
  //       return;
  //     }

  //     // Step 2: Handle File Upload (if exists)
  //     if (parameters.file) {
  //       const formData = new FormData();
  //       formData.append("messaging_product", "whatsapp");
  //       formData.append("file", parameters.file);
  //       formData.append("description", "Attachment");
  //       fileResult = await WhatsAppAPI.createFile(contactData.id, formData);

  //       // console.log("fileResult",fileResult);
  //       const uploadResponse = await WhatsAppAPI.uploadDocumentWithApi(
  //         formData,
  //         selectedWhatsAppSetting
  //       );

  //       if (uploadResponse?.id) {
  //         documentId = uploadResponse.id; // Bind the received ID
  //       }
  //     } else if (
  //       selectedTemplateName.header !== "TEXT" &&
  //       selectedTemplateName.header &&
  //       selectedTemplateName.header_body
  //     ) {
  //       const fileUrl = selectedTemplateName.header_body;
  //       let fileBlob = "";
  //       if (selectedTemplateName.header === "DOCUMENT") {
  //         const obj = { url: fileUrl };
  //         fileBlob = await WhatsAppAPI.pdfData(obj, selectedWhatsAppSetting);
  //         documentId = fileBlob;
  //       } else {
  //         fileBlob = await fetchFile(fileUrl);
  //         const fileName =
  //           fileUrl.split("/").pop().split("?")[0] ||
  //           (selectedTemplateName.header === "DOCUMENT"
  //             ? "application.pdf"
  //             : "file");
  //         const file = new File([fileBlob], fileName, { type: fileBlob.type });

  //         const formData = new FormData();
  //         formData.append("messaging_product", "whatsapp");
  //         formData.append("file", file);
  //         formData.append(
  //           "description",
  //           `Header ${selectedTemplateName.header} Template`
  //         );

  //         const documentResult = await WhatsAppAPI.uploadDocumentWithApi(
  //           formData,
  //           selectedWhatsAppSetting
  //         );
  //         documentId = documentResult.id;
  //       }
  //     }

  //     // Step 3: Construct Message Payload
  //     const reqBody = {
  //       messaging_product: "whatsapp",
  //       to: fullNumber,
  //       type: "template",
  //       // category: selectedTemplateName.category,
  //       template: {
  //         name: selectedTemplateName.name,
  //         language: { code: selectedTemplateName.language },
  //         components: [
  //           {
  //             type: "header",
  //             parameters: documentId
  //               ? [
  //                   {
  //                     type: selectedTemplateName.header.toLowerCase(),
  //                     [selectedTemplateName.header.toLowerCase()]: {
  //                       id: documentId,
  //                       ...(selectedTemplateName.header === "DOCUMENT" &&
  //                       parameters.file &&
  //                       parameters.file.name
  //                         ? { filename: parameters.file.name }
  //                         : {}),
  //                     },
  //                   },
  //                 ]
  //               : [],
  //           },
  //           {
  //             type: "body",
  //             parameters:
  //               selectedTemplateName.category !== "AUTHENTICATION"
  //                 ? (() => {
  //                     const matches =
  //                       selectedTemplateName.message_body?.match(
  //                         /\{\{(\d+)\}\}/g
  //                       ) || [];
  //                     const uniqueIndices = [];
  //                     matches.forEach((match) => {
  //                       const idx = match.replace(/\{\{|\}\}/g, "");
  //                       if (!uniqueIndices.includes(idx))
  //                         uniqueIndices.push(idx);
  //                     });
  //                     return uniqueIndices.map((idx) => ({
  //                       type: "text",
  //                       text: parameters[idx] || "",
  //                     }));
  //                   })()
  //                 : [],
  //           },
  //         ],
  //       },
  //     };

  //     if (
  //       selectedTemplateName.example_body_text &&
  //       selectedTemplateName.category === "AUTHENTICATION"
  //     ) {
  //       // if (selectedTemplateName.example_body_text) {
  //       reqBody.template.components[1].parameters.push({
  //         type: "text",
  //         text: selectedTemplateName.example_body_text,
  //       });
  //     }

  //     if (
  //       selectedTemplateName.example_body_text &&
  //       selectedTemplateName.category === "AUTHENTICATION"
  //     ) {
  //       reqBody.template.components.push({
  //         type: "button",
  //         sub_type: "url",
  //         index: 0,
  //         parameters: [
  //           { type: "text", text: selectedTemplateName.example_body_text },
  //         ],
  //       });
  //     }

  //     // Step 4: Send Message
  //     const result = await WhatsAppAPI.sendWhatsAppTemplateMessage(
  //       reqBody,
  //       selectedWhatsAppSetting,
  //       selectedTemplateName.category
  //     );
  //     // console.log("result",result);
  //     // debugger;
  //     if (result.error) {
  //       const details =
  //         result.error?.error_data?.details ||
  //         result.error?.message ||
  //         result.error?.body ||
  //         JSON.stringify(result.error);
  //       toast.error(`Error: ${details}`);
  //     } else {
  //       const messageId = result.messages[0].id;
  //       // console.log("messageId",messageId);
  //       const msgHistoryResult = await WhatsAppAPI.insertMsgHistoryRecords({
  //         parent_id: contactData.id || null,
  //         name: contactData?.contactname || "",
  //         message_template_id: msgResult.id || null,
  //         whatsapp_number: fullNumber,
  //         message: "",
  //         status: "Outgoing",
  //         recordtypename: filterData?.recordType || "",
  //         file_id: fileResult?.records[0]?.id || null,
  //         is_read: true,
  //         business_number: selectedWhatsAppSetting,
  //         message_id: messageId,
  //         interactive_id: null,
  //       });
  //       const {
  //         sendToAdmin,
  //         file,
  //         file_id,
  //         whatsapp_number_admin,
  //         ...restParameters
  //       } = parameters || {};

  //       if (Object.keys(restParameters).length > 0) {
  //         const campaign_template_params = {
  //           campaign_id: null,
  //           body_text_params: restParameters,
  //           msg_history_id: msgHistoryResult?.id || null,
  //           file_id: fileResult?.records[0]?.id || null,
  //           whatsapp_number_admin: userInfo?.whatsapp_number || null,
  //         };

  //         const campaignparamsResult =
  //           await WhatsAppAPI.insertCampaignParamsRecords(
  //             campaign_template_params
  //           );
  //       }

  //       if (userInfo?.has_wallet) {
  //         const balanceRes = await WhatsAppAPI.createWalletTransaction({
  //           amount: price,
  //           reason: `debited for sending template to ${contactData?.contactname}`,
  //           type: "debit",
  //           status: "paid",
  //           tenantcode: userInfo.tenantcode,
  //         });
  //       }
  //     }

  //     // Step 5: Send to Admin (if enabled)
  //     if (parameters.sendToAdmin && userInfo?.whatsapp_number) {
  //       const adminPhone = `${(userInfo?.country_code || "91").replace(
  //         /^\+?/,
  //         "+"
  //       )}${userInfo?.whatsapp_number?.replace(/^\+/, "")}`;

  //       const adminReqBody = { ...reqBody, to: adminPhone };
  //       const adminResult = await WhatsAppAPI.sendWhatsAppTemplateMessage(
  //         adminReqBody,
  //         selectedWhatsAppSetting,
  //         selectedTemplateName.category
  //       );

  //       if (!adminResult.error) {
  //         const adminMessageId = adminResult.messages[0].id;
  //         const adminMsgHistoryResult =
  //           await WhatsAppAPI.insertMsgHistoryRecords({
  //             parent_id: userInfo.id,
  //             name: userInfo.username,
  //             message_template_id: msgResult.id || null,
  //             whatsapp_number: adminPhone,
  //             message: "",
  //             status: "Outgoing",
  //             recordtypename: filterData?.recordType || "",
  //             file_id: fileResult?.records[0]?.id || null,
  //             is_read: true,
  //             business_number: selectedWhatsAppSetting,
  //             message_id: adminMessageId,
  //             interactive_id: null,
  //           });

  //         if (userInfo?.has_wallet) {
  //           const balanceRes = await WhatsAppAPI.createWalletTransaction({
  //             amount: price,
  //             reason: `debited for sending template to ${userInfo?.username}`,
  //             type: "debit",
  //             status: "paid",
  //             tenantcode: userInfo.tenantcode,
  //           });
  //         }

  //         const {
  //           sendToAdmin,
  //           file,
  //           file_id,
  //           whatsapp_number_admin,
  //           ...restParameters
  //         } = parameters || {};

  //         if (Object.keys(restParameters).length > 0) {
  //           // Only proceed if restParameters has values
  //           const campaign_template_params = {
  //             campaign_id: null,
  //             body_text_params: restParameters,
  //             msg_history_id: adminMsgHistoryResult?.id || null,
  //             file_id: fileResult?.records[0]?.id || null,
  //             whatsapp_number_admin: userInfo?.whatsapp_number || null,
  //           };

  //           const campaignparamsResult =
  //             await WhatsAppAPI.insertCampaignParamsRecords(
  //               campaign_template_params
  //             );
  //         } else {
  //           console.warn(
  //             "Skipping insertCampaignParamsRecords: restParameters is empty"
  //           );
  //         }
  //       }
  //     }
  //   } catch (error) {
  //     toast.error("Template send limit reached");
  //     setIsSpinner(true);
  //   } finally {
  //     setIsSpinner(true);
  //     refreshData();
  //   }
  // };

  const { file, sendToAdmin, button_variables, ...filteredParams } = parameters;
  const matches =
    selectedTemplateName?.message_body?.match(/\{\{(\d+)\}\}/g) || [];
  const uniqueIndices = [];
  matches.forEach((match) => {
    const idx = match.replace(/\{\{|\}\}/g, "");
    if (!uniqueIndices.includes(idx)) uniqueIndices.push(idx);
  });
  const messageMatches = uniqueIndices;
  const bodyParamKeys = Object.keys(filteredParams).filter((k) =>
    /^\d+$/.test(k),
  );
  const hasExactMatch =
    JSON.stringify([...bodyParamKeys].sort()) ===
    JSON.stringify([...messageMatches].sort());
  const allValuesFilled =
    bodyParamKeys.length === 0 ||
    bodyParamKeys.every(
      (key) =>
        filteredParams[key] !== undefined &&
        filteredParams[key] !== null &&
        String(filteredParams[key]).trim() !== "",
    );
  const finalCheck = hasExactMatch && allValuesFilled;
  //   const isFormValid = Boolean(selectedTemplateName) && finalCheck;
  const requiresFile =
    selectedTemplateName?.header && selectedTemplateName?.header !== "TEXT";

  function isCarouselFormValid(selectedTemplateName, parameters) {
    if (!selectedTemplateName || !Array.isArray(selectedTemplateName.cards))
      return false;

    // Main body validation
    let mainValid = true;
    let mainMatches = [];
    let mainUniqueIndices = [];
    if (selectedTemplateName.message_body) {
      mainMatches =
        selectedTemplateName.message_body.match(/\{\{([^}]+)\}\}/g) || [];
      mainUniqueIndices = [
        ...new Set(mainMatches.map((m) => m.replace(/\{\{|\}\}/g, ""))),
      ];
      mainValid =
        mainUniqueIndices.length === 0 ||
        mainUniqueIndices.every(
          (idx) =>
            parameters.main &&
            typeof parameters.main[idx] === "string" &&
            parameters.main[idx].trim() !== "",
        );
    }

    // Cards validation
    let cardsValid = selectedTemplateName.cards.every((card, idx) => {
      const cardParams = parameters[idx] || {};

      // Check if card has file requirements
      const requiresFile = card.header_type && card.header_type !== "TEXT";
      const hasFile =
        cardParams.file &&
        (cardParams.file instanceof File ||
          (cardParams.fileURL && cardParams.fileType));

      // If card requires file but doesn't have one, it's invalid
      if (requiresFile && !hasFile) {
        return false;
      }

      // Check body text parameters
      const matches =
        (typeof card.body_text === "string" ? card.body_text : "").match(
          /\{\{([^}]+)\}\}/g,
        ) || [];
      const uniqueIndices = [
        ...new Set(matches.map((m) => m.replace(/\{\{|\}\}/g, ""))),
      ];

      const bodyParamsValid =
        uniqueIndices.length === 0 ||
        uniqueIndices.every(
          (paramIdx) =>
            cardParams[paramIdx] &&
            typeof cardParams[paramIdx] === "string" &&
            cardParams[paramIdx].trim() !== "",
        );

      return bodyParamsValid;
    });

    return mainValid && cardsValid;
  }

  function hasEmptyParameter(components) {
    return components.some(
      (comp) =>
        Array.isArray(comp.parameters) &&
        comp.parameters.some(
          (param) => typeof param.text === "string" && param.text.trim() === "",
        ),
    );
  }

  function hasEmptyParameterDeep(components) {
    // Check top-level components
    if (
      components.some(
        (comp) =>
          Array.isArray(comp.parameters) &&
          comp.parameters.some(
            (param) =>
              typeof param.text === "string" && param.text.trim() === "",
          ),
      )
    )
      return true;
    // Check carousel cards
    const carousel = components.find((comp) => comp.type === "carousel");
    if (carousel && Array.isArray(carousel.cards)) {
      return carousel.cards.some(
        (card) =>
          Array.isArray(card.components) &&
          card.components.some(
            (comp) =>
              Array.isArray(comp.parameters) &&
              comp.parameters.some(
                (param) =>
                  typeof param.text === "string" && param.text.trim() === "",
              ),
          ),
      );
    }
    return false;
  }

  const buttonVars = selectedTemplateName
    ? getButtonVariables(selectedTemplateName)
    : [];
  const buttonVarsFilled =
    buttonVars.length === 0 ||
    buttonVars.every(
      (v) => (buttonVariableValues[v.variableIndex] || "").trim() !== "",
    );

  const isFormValid =
    selectedTemplateName?.template_type === "carousel"
      ? isCarouselFormValid(selectedTemplateName, parameters) &&
        buttonVarsFilled
      : Boolean(selectedTemplateName) &&
        finalCheck &&
        (!requiresFile || Boolean(parameters.file)) &&
        buttonVarsFilled;

  return (
    <>
      <Modal show={show} animation={false} size="lg" centered>
        <Modal.Header closeButton onClick={onHide}>
          <Modal.Title id="contained-modal-title-vcenter">
            Send WhatsApp Template
          </Modal.Title>
        </Modal.Header>
        {isSpinner ? (
          <>
            <Modal.Body style={{ maxHeight: "70vh", overflow: "auto" }}>
              <Form noValidate>
                <Row className="p-2 mb-3">
                  <Col lg={12} sm={12} xs={12}>
                    {/* Category Filter Dropdown */}
                    <Form.Group className="mb-3">
                      <Form.Label
                        className="form-view-label"
                        htmlFor="categoryFilter"
                      >
                        Select Template Category
                      </Form.Label>
                      <Form.Select
                        style={{ height: "36px" }}
                        aria-label="Select Template Category"
                        name="categoryFilter"
                        onChange={handleCategoryChange}
                      >
                        {categories.map((category) => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col lg={12} sm={12} xs={12}>
                    <Form.Group className="mb-3">
                      <Form.Label
                        className="form-view-label"
                        htmlFor="formBasicFirstName"
                      >
                        Template Name
                      </Form.Label>
                      <Form.Select
                        required
                        style={{ height: "36px" }}
                        aria-label="select name"
                        name="templateName"
                        onChange={handleChange}
                        placeholder="Select Template Name"
                        // value={templateName}
                      >
                        <option value="">Select Template Name</option>
                        {filteredTemplates?.map((template) => (
                          <option key={template.id} value={template.id}>
                            {template?.message_body?.match(/\{\{(\d+)\}\}/g)
                              ?.length > 0
                              ? `${template.templatename} {{-}} `
                              : template.templatename}

                            {/* {template.templatename} */}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col lg={12} sm={12} xs={12}>
                    <TemplatePreview
                      template={selectedTemplateName}
                      onParametersChange={handleParametersChange}
                      //tamplateparms={tamplateparms}
                      setParameters={setParameters}
                      parameters={parameters}
                    />
                    {selectedTemplateName &&
                      getButtonVariables(selectedTemplateName).length > 0 && (
                        <Form.Group className="mb-3 mt-2">
                          <Form.Label className="form-view-label">
                            Button dynamic values
                          </Form.Label>
                          {getButtonVariables(selectedTemplateName).map(
                            (btnVar) => (
                              <Form.Group
                                key={btnVar.variableIndex}
                                className="mb-2"
                              >
                                <Form.Label
                                  className="form-view-label small"
                                  style={{ marginBottom: "4px" }}
                                >
                                  {btnVar.label}
                                </Form.Label>
                                <Form.Control
                                  type="text"
                                  placeholder={`Enter value for {{${btnVar.variableIndex}}}`}
                                  value={
                                    buttonVariableValues[
                                      btnVar.variableIndex
                                    ] || ""
                                  }
                                  onChange={(e) =>
                                    setButtonVariableValues((prev) => ({
                                      ...prev,
                                      [btnVar.variableIndex]: e.target.value,
                                    }))
                                  }
                                />
                              </Form.Group>
                            ),
                          )}
                        </Form.Group>
                      )}
                  </Col>
                </Row>
              </Form>
            </Modal.Body>

            <Modal.Footer>
              <button className="btn btn-light" onClick={onHide}>
                Close
              </button>
              <button
                className="btn btn-outline-secondary"
                onClick={handleSubmit}
                disabled={!isFormValid}
              >
                Send Message
              </button>
            </Modal.Footer>
          </>
        ) : (
          <div className="sk-cube-grid">
            <div className="sk-cube sk-cube1"></div>
            <div className="sk-cube sk-cube2"></div>
            <div className="sk-cube sk-cube3"></div>
            <div className="sk-cube sk-cube4"></div>
            <div className="sk-cube sk-cube5"></div>
            <div className="sk-cube sk-cube6"></div>
            <div className="sk-cube sk-cube7"></div>
            <div className="sk-cube sk-cube8"></div>
            <div className="sk-cube sk-cube9"></div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default TemplateModal;
